import { TournamentMatch } from "@mixitone/models";
import type { Match, TournamentState } from "tournament-formats/types";

/** Bare reducer slot id (e.g. `r0:m0`), never the composite projected match id. */
export function getMatchSlotId(
  match: Pick<TournamentMatch, "id" | "format_data"> | { id?: string; format_data?: Record<string, unknown> },
): string {
  const fromMeta = match.format_data?.slot_id;
  if (typeof fromMeta === "string" && fromMeta.length > 0) return fromMeta;
  const id = match.id || "";
  const separator = id.indexOf("/");
  if (separator >= 0) return id.slice(separator + 1);
  return id;
}

/** Globally unique projected match id so ModelRegistry does not collapse cross-competition slots. */
export function getProjectedMatchId(competitionId: string, slotId: string): string {
  return `${competitionId}/${slotId}`;
}

function winnersAndLosersForMatch(match: Match): { winners: string[]; losers: string[] } {
  const winners: string[] = [];
  const losers: string[] = [];
  if (match[0]?.[0]) {
    if (match[0][1]) {
      winners.push(match[0][0]);
    } else if (match[1]?.[1]) {
      losers.push(match[0][0]);
    }
  }
  if (match[1]?.[0]) {
    if (match[1][1]) {
      winners.push(match[1][0]);
    } else if (match[0]?.[1]) {
      losers.push(match[1][0]);
    }
  }
  return { winners, losers };
}

export function projectMatchesFromState(params: {
  state: TournamentState;
  competitionId: string;
  accountId?: string;
  clubId?: string;
}): TournamentMatch[] {
  const { state, competitionId, accountId, clubId } = params;

  const projected: TournamentMatch[] = [];
  let globalMatchIndex = 0;

  for (let roundIndex = 0; roundIndex < state.rounds.length; roundIndex++) {
    const round = state.rounds[roundIndex];
    for (let matchIndex = 0; matchIndex < round.length; matchIndex++) {
      const match = round[matchIndex];
      if (!match) continue;
      const metadata = (match[2] || {}) as Record<string, any>;
      const slotId = String(metadata.slot_id || `r${roundIndex}:m${matchIndex}`);
      const entries = [match[0]?.[0], match[1]?.[0]].filter((entry): entry is string => !!entry);
      const winnersLosers = winnersAndLosersForMatch(match);
      const explicitStatus = metadata.status as TournamentMatch["status"] | undefined;
      const status: TournamentMatch["status"] =
        explicitStatus ||
        (winnersLosers.winners.length > 0
          ? "completed"
          : entries.length > 1
            ? metadata.court_id
              ? "ready"
              : "ready"
            : "pending");

      const projectedMatch = new TournamentMatch({
        id: getProjectedMatchId(competitionId, slotId),
        account_id: accountId,
        club_id: clubId,
        tournament_competition_id: competitionId,
        round: roundIndex,
        match_index: globalMatchIndex++,
        status,
        court_id: metadata.court_id ? String(metadata.court_id) : undefined,
        started_at: metadata.started_at,
        completed_at: metadata.completed_at,
        entries: {
          current: entries,
          winners: winnersLosers.winners,
          losers: winnersLosers.losers,
          score:
            typeof metadata.score === "object" && metadata.score !== null
              ? (metadata.score as Record<any, any>)
              : {},
        },
        format_data: {
          ...metadata,
          slot_id: slotId,
        },
      } as any);
      projected.push(projectedMatch);
    }
  }

  return projected;
}
